import { type ReactNode, useEffect, useRef, useState } from 'react';
import { BsCheck, BsCheckAll, BsImage, BsPencilSquare, BsPinAngle } from 'react-icons/bs';
import { PiTrash } from 'react-icons/pi';
import { TbPinnedOff } from 'react-icons/tb';

import type { MediaStatus } from '@/common/media';
import { ActionsButton, Avatar } from '@/components';
import { Button, ButtonOutlined } from '@/components/Button';
import { TextArea } from '@/components/Inputs';

import styles from './ChatMessage.module.scss';

export type ChatAttachment = {
  id: string;
  name: string;
  url?: string;
  link?: string;
  type?: string;
  size?: number;
  isImage?: boolean;
  isVideo?: boolean;
  mediaStatus?: MediaStatus | null;
  /** The cover's private link. */
  cover?: null | string;
  /** The backup rendition's private link, played when the main one cannot be. */
  backup?: null | string;
  /** An image's copy sized for the thread; `link` stays the original for the preview. */
  preview?: null | string;
};

export type ChatMessageData = {
  id: string;
  sender: string;
  text: string;
  timestamp: number;
  fromSelf?: boolean;
  messageType?: string;
  attachments?: ChatAttachment[];
  creatorId?: string;
  isRead?: boolean;
  isEdited?: boolean;
  role?: string;
};

export interface ChatMessageProps {
  message: ChatMessageData;
  avatarSrc?: string;
  showDateLabel?: boolean;
  isPinned?: boolean;
  onPin?: (id: string) => void;
  onUnpin?: (id: string) => void;
  onEdit?: (id: string, newText: string) => void;
  onDelete?: (id: string) => void;
  onAddToGallery?: (message: ChatMessageData) => void;
  renderAttachment?: (att: ChatAttachment) => ReactNode;
  onClick?: (id: string) => void;
  chatType?: string;
}

export const ChatMessage = ({
  message,
  avatarSrc,
  showDateLabel,
  isPinned,
  onPin,
  onUnpin,
  onEdit,
  onDelete,
  onAddToGallery,
  renderAttachment,
  onClick,
  chatType,
}: ChatMessageProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const editTextAreaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isEditing && editTextAreaRef.current) {
      const el = editTextAreaRef.current;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    }
  }, [isEditing]);

  const isSelf = message.fromSelf === true;
  const isHistory = message.messageType === 'history';

  const handleEditStart = () => {
    setIsEditing(true);
    setEditText(message.text);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditText('');
  };

  const handleSaveEdit = () => {
    if (editText.trim() === '') return;
    onEdit?.(message.id, editText);
    setIsEditing(false);
    setEditText('');
  };

  const timeLabel =
    message.messageType === 'pending'
      ? 'Sending…'
      : message.messageType === 'failed'
        ? 'Failed'
        : `${formatTime(message.timestamp)}${message.isEdited ? ' (edited)' : ''}`;

  const hasImageAttachment = !!message.attachments?.some(
    (a) => a.isImage || (a.type ?? '') === 'image' || (a.type ?? '').startsWith('image/'),
  );
  const galleryAction =
    onAddToGallery && hasImageAttachment
      ? {
          label: 'Add to Memories',
          icon: <BsImage />,
          onClick: () => onAddToGallery(message),
        }
      : null;
  const hasActions = onPin || onUnpin || onEdit || onDelete || galleryAction;
  const pinAction =
    isPinned && onUnpin
      ? { label: 'Unpin', icon: <TbPinnedOff />, onClick: () => onUnpin(message.id) }
      : onPin
        ? { label: 'Pin', icon: <BsPinAngle />, onClick: () => onPin(message.id) }
        : null;
  const actionItems = isSelf
    ? [
        ...(galleryAction ? [galleryAction] : []),
        ...(pinAction ? [pinAction] : []),
        ...(onEdit ? [{ label: 'Edit', icon: <BsPencilSquare />, onClick: handleEditStart }] : []),
        ...(onDelete
          ? [
              {
                label: 'Delete',
                icon: <PiTrash />,
                onClick: () => onDelete(message.id),
                variant: 'danger' as const,
              },
            ]
          : []),
      ]
    : [...(galleryAction ? [galleryAction] : []), ...(pinAction ? [pinAction] : [])];

  return (
    <div>
      {showDateLabel ? (
        <div className={styles.chatDateLabel}>{formatDateLabel(message.timestamp)}</div>
      ) : null}
      <div
        id={`message-${message.id}`}
        className={isSelf ? styles.chatMessageRowSelf : styles.chatMessageRow}
        onClick={onClick ? () => onClick(message.id) : undefined}
        style={onClick ? { cursor: 'pointer' } : undefined}
      >
        {!isSelf && !isHistory ? (
          <Avatar src={avatarSrc} firstName={message.sender} size={28} />
        ) : null}
        <div
          className={
            isHistory
              ? styles.chatBubbleHistory
              : isSelf
                ? styles.chatBubbleSelf
                : styles.chatBubble
          }
          style={
            message.messageType === 'pending'
              ? { opacity: 0.6 }
              : message.messageType === 'failed'
                ? { opacity: 0.6, borderLeft: '2px solid #ef4444' }
                : undefined
          }
          title={message.sender}
        >
          {!isHistory && (
            <span className={styles.chatSender}>
              <p>
                {isSelf ? 'You' : message.sender}{' '}
                <span
                  style={{ fontWeight: 'lighter', margin: '0 4px', color: 'var(--text-muted)' }}
                >
                  {message.role && chatType !== 'one_to_one' ? message.role : null}
                </span>
              </p>
              {hasActions && actionItems.length > 0 && (
                <ActionsButton variant={isSelf ? 'lite' : 'dark'} items={actionItems} />
              )}
            </span>
          )}
          <div className={styles.textContainer}>
            {isEditing ? (
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}
                onClick={(e) => e.stopPropagation()}
              >
                <TextArea
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  rows={3}
                  ref={editTextAreaRef}
                />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <ButtonOutlined onClick={handleCancelEdit}>Cancel</ButtonOutlined>
                  <Button onClick={handleSaveEdit} disabled={editText.trim() === ''}>
                    Save
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <p className={styles.chatText}>{message.text}</p>
                {!message.attachments?.length && (
                  <span className={styles.chatMeta}>
                    {isPinned && <BsPinAngle style={{ marginRight: 3, flexShrink: 0 }} />}
                    {timeLabel}
                    {isSelf &&
                      message.messageType !== 'pending' &&
                      message.messageType !== 'failed' &&
                      (message.isRead ? (
                        <BsCheckAll className={styles.chatReadIcon} />
                      ) : (
                        <BsCheck className={styles.chatReadIcon} />
                      ))}
                  </span>
                )}
              </>
            )}
          </div>
          {message.attachments?.length ? (
            <>
              <div className={styles.chatAttachments}>
                {message.attachments.map((att) => renderAttachment?.(att))}
              </div>
              <span className={styles.chatMeta}>
                {isPinned && <BsPinAngle style={{ marginRight: 3, flexShrink: 0 }} />}
                {timeLabel}
                {isSelf &&
                  message.messageType !== 'pending' &&
                  message.messageType !== 'failed' &&
                  (message.isRead ? (
                    <BsCheckAll className={styles.chatReadIcon} />
                  ) : (
                    <BsCheck className={styles.chatReadIcon} />
                  ))}
              </span>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const formatDateLabel = (timestamp: number) => {
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const isToday = date.toDateString() === today.toDateString();
  const isYesterday = date.toDateString() === yesterday.toDateString();

  if (isToday) return 'Today';
  if (isYesterday) return 'Yesterday';

  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
  };
  return date.toLocaleDateString('en-US', options);
};

const formatTime = (timestamp: number) => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};
